import { useState } from "react";
import { createFileRoute, useParams } from "@tanstack/react-router";
import { useAuth } from "@clerk/clerk-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Dialog } from "@base-ui/react/dialog";
import { Input } from "@base-ui/react/input";
import { Field } from "@base-ui/react/field";
import { IconPlus, IconBriefcase } from "@tabler/icons-react";
import { positionsQueryOptions } from "../../../api/queries";
import { createPosition } from "../../../api/positions";
import { parseOrgId } from "../../../lib/slug";

export const Route = createFileRoute("/organization/$slug/recruitment")({
  component: Recruitment,
});

function Recruitment() {
  const { getToken } = useAuth();
  const { slug } = useParams({ from: "/organization/$slug" });
  const orgId = parseOrgId(slug);
  const queryClient = useQueryClient();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  const { data, isLoading } = useQuery(positionsQueryOptions(getToken, orgId));
  const positions = data?.positions ?? [];

  const mutation = useMutation({
    mutationFn: () =>
      createPosition(
        getToken,
        orgId,
        name.trim(),
        description.trim() || undefined
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["positions", orgId] });
      setName("");
      setDescription("");
      setDialogOpen(false);
    },
  });

  function handleOpenChange(open: boolean) {
    setDialogOpen(open);
    if (!open) {
      setName("");
      setDescription("");
      mutation.reset();
    }
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-primary-darker">
            Recruitment
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage open positions and applications.
          </p>
        </div>

        <Dialog.Root open={dialogOpen} onOpenChange={handleOpenChange}>
          <Dialog.Trigger className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-darker transition-colors">
            <IconPlus size={16} />
            Create Position
          </Dialog.Trigger>
          <Dialog.Portal>
            <Dialog.Backdrop className="fixed inset-0 bg-black/40" />
            <Dialog.Popup className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md rounded-xl border border-gray-200 bg-white p-6 shadow-lg">
              <Dialog.Title className="text-lg font-semibold text-gray-900">
                Create Position
              </Dialog.Title>
              <Dialog.Description className="mt-1 text-sm text-gray-500">
                Add a new position to recruit for.
              </Dialog.Description>

              <form
                className="mt-4 space-y-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (name.trim()) {
                    mutation.mutate();
                  }
                }}
              >
                <Field.Root>
                  <Field.Label className="block text-sm font-medium text-gray-700">
                    Name
                  </Field.Label>
                  <Input
                    required
                    value={name}
                    onValueChange={setName}
                    placeholder="e.g. Brand designer"
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </Field.Root>

                <div>
                  <label
                    htmlFor="position-description"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Description
                  </label>
                  <textarea
                    id="position-description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Optional description of the role"
                    rows={3}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary resize-none"
                  />
                </div>

                {mutation.isError && (
                  <p className="text-sm text-red-600">
                    Failed to create position. Please try again.
                  </p>
                )}

                <div className="flex justify-end gap-2">
                  <Dialog.Close className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
                    Cancel
                  </Dialog.Close>
                  <button
                    type="submit"
                    disabled={!name.trim() || mutation.isPending}
                    className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-darker disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {mutation.isPending ? "Creating…" : "Create"}
                  </button>
                </div>
              </form>
            </Dialog.Popup>
          </Dialog.Portal>
        </Dialog.Root>
      </div>

      {/* Position list */}
      <div className="mt-6">
        {isLoading ? (
          <p className="text-sm text-gray-500">Loading positions…</p>
        ) : positions.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 py-12">
            <IconBriefcase size={40} stroke={1.5} className="text-gray-400" />
            <p className="mt-3 text-sm font-medium text-gray-900">
              No positions yet
            </p>
            <p className="mt-1 text-sm text-gray-500">
              Create your first position to start recruiting.
            </p>
          </div>
        ) : (
          <table className="w-full rounded-xl border border-gray-200 bg-white text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-gray-500">
                <th className="px-4 py-3 font-medium">Position</th>
                <th className="px-4 py-3 font-medium">Description</th>
                <th className="px-4 py-3 font-medium">Members</th>
              </tr>
            </thead>
            <tbody>
              {positions.map((position) => (
                <tr
                  key={position.id}
                  className="border-b border-gray-200 last:border-b-0"
                >
                  <td className="px-4 py-3 font-medium text-gray-900">
                    {position.name}
                  </td>
                  <td className="px-4 py-3 text-gray-500">
                    {position.description || "—"}
                  </td>
                  <td className="px-4 py-3 text-gray-500">—</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
